/**
 * スプリングのプリセット。
 *
 * Apple は物理三点セット (mass/stiffness/damping) ではなく、デザイナが扱える 2 つの
 * パラメータで考える:
 *
 * - damping ratio: オーバーシュート量。1.0 = 臨界減衰 (跳ねない)、1.0 未満で跳ねる。
 * - response: 目標に到達する速さ (秒)。duration ではない — スプリングに固定の長さは無く、
 *   収束時間はパラメータから決まる。
 *
 * Motion の `bounce` + `duration` はこの damping + response にほぼ対応する。
 *
 * 既定は damping 1.0 (bounce: 0)。跳ねは「ジェスチャ自体が勢いを持っていたとき」だけ足す。
 * フェードインしただけのメニューが跳ねるのは間違いだが、弾いたカードが跳ねるのは正しい。
 */
export const spring = {
  /** UI 既定。臨界減衰で上品に収まる。 */
  default: { type: "spring", bounce: 0, duration: 0.35 },
  /** 位置の移動 (Apple の PiP 相当: damping 1.0 / response 0.4)。 */
  move: { type: "spring", bounce: 0, duration: 0.4 },
  /** ドロワー / シート (damping 0.8 / response 0.3)。 */
  sheet: { type: "spring", bounce: 0.2, duration: 0.3 },
  /** 弾き・投げの後。勢いを引き継ぐのでわずかに跳ねる。 */
  momentum: { type: "spring", bounce: 0.2, duration: 0.4 },
} as const;

/**
 * 慣性の着地点を予測する。速度から「どこで止まるか」を出して、そこに一番近い
 * スナップ先を選ぶ。放した位置から最寄りに吸わせるのではなく、投げた先に飛ばすための計算。
 *
 * 教科書的な v^2/(2a) ではなく、Apple のサンプルコードと同じ指数減衰の形を使う。
 *
 * @param initialVelocity px/s
 * @param decelerationRate 通常のスクロール感で 0.998、キビキビさせるなら 0.99
 */
export const projectDecay = (initialVelocity: number, decelerationRate = 0.998): number =>
  ((initialVelocity / 1000) * decelerationRate) / (1 - decelerationRate);

/**
 * 境界でのラバーバンド。端で硬く止めると「固まった」と読めるが、
 * 進むほど抵抗が増えると「反応はしているがこの先は無い」と読める。
 */
export const rubberband = (overshoot: number, dimension: number, constant = 0.55): number =>
  (overshoot * dimension * constant) / (dimension + constant * Math.abs(overshoot));
